import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  User,
  Sparkles,
  Package,
  RotateCcw,
  ArrowRight,
  Moon,
  Gem,
  Star,
  ShieldCheck,
  ScrollText,
  MessageCircleMore,
} from "lucide-react";
import recommendationService from "../../services/recommendationService";
import {
  QUIZ_STEPS,
  QUIZ_STATE_KEY,
  RECOMMENDATION_KEY,
  USER_PROFILE_KEY,
} from "./constants";
import { generateShaktiWhatsappUrl } from "./Message";

function WelcomeScreen({ onStartNew, onContinue }) {
  const hasPreviousState = localStorage.getItem(QUIZ_STATE_KEY);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-4 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          <h3 className="text-sm font-bold">Find Your Perfect Rudraksha</h3>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-white/95">
          Let us help you discover the ideal Rudraksha based on your spiritual
          goals and birth details.
        </p>
      </div>

      <div className="space-y-2">
        <button
          onClick={onStartNew}
          className="w-full rounded-xl bg-primary px-4 py-3 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
        >
          Start New Consultation
        </button>

        {hasPreviousState && (
          <button
            onClick={onContinue}
            className="w-full rounded-xl border border-outline-variant/70 bg-surface-container-low px-4 py-3 text-xs font-semibold text-on-surface transition-colors hover:bg-surface-container-low/80"
          >
            Continue Previous Session
          </button>
        )}
      </div>
    </div>
  );
}

function UserInfoForm({ onSubmit, onCancel, initialData }) {
  const [name, setName] = useState(initialData?.name || "");
  const [dob, setDob] = useState(initialData?.dob || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() && dob) {
      onSubmit({ name: name.trim(), dob });
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-4 text-white">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5" />
          <h3 className="text-sm font-bold">Tell Us About Yourself</h3>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-white/95">
          Your birth details help us provide personalized recommendations.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-on-surface">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            className="w-full rounded-lg border border-outline-variant/70 bg-surface-container-low px-3 py-2 text-xs text-on-surface placeholder:text-on-surface/50 focus:border-primary focus:outline-none"
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-on-surface">
            Date of Birth
          </label>
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            className="w-full rounded-lg border border-outline-variant/70 bg-surface-container-low px-3 py-2 text-xs text-on-surface focus:border-primary focus:outline-none"
            required
          />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg border border-outline-variant/70 bg-surface-container-low px-3 py-2 text-xs font-medium text-on-surface transition-colors hover:bg-surface-container-low/80"
          >
            Back
          </button>
          <button
            type="submit"
            className="flex-1 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-primary/90"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}

function QuizView({ questions, answers, onAnswer, onBack, onSubmit }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.key] || "";

  const handleAnswer = (answer) => {
    onAnswer(currentQuestion.key, answer);
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      onSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else {
      onBack();
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <h3 className="text-sm font-bold">Spiritual Goals Quiz</h3>
          </div>
          <span className="text-xs text-white/80">
            {currentQuestionIndex + 1}/{questions.length}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-on-surface">
          {currentQuestion?.label}
        </h4>

        <div className="space-y-2">
          {(currentQuestion?.options || []).map((option) => (
            <button
              key={option}
              onClick={() => handleAnswer(option)}
              className={`w-full rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                currentAnswer === option
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-outline-variant/70 bg-surface-container-low text-on-surface hover:bg-surface-container-low/80"
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        <button
          onClick={handleBack}
          className="w-full rounded-lg border border-outline-variant/70 bg-surface-container-low px-3 py-2 text-xs font-medium text-on-surface transition-colors hover:bg-surface-container-low/80"
        >
          Back
        </button>
      </div>
    </div>
  );
}

function LoadingView({ message = "Finding your perfect Rudraksha..." }) {
  return (
    <div className="flex flex-col items-center justify-center space-y-4 py-8">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <p className="text-xs text-on-surface/70">{message}</p>
    </div>
  );
}

// function RecommendationView({
//   recommendations,
//   onViewProduct,
//   onReset,
//   onNavigateToProduct,
// }) {
//   const handleProductClick = (product) => {
//     onNavigateToProduct(product);
//     onViewProduct(product);
//   };

//   return (
//     <div className="space-y-4">
//       <div className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-4 text-white">
//         <div className="flex items-center gap-2">
//           <Package className="h-5 w-5" />
//           <h3 className="text-sm font-bold">Your Recommendations</h3>
//         </div>
//         <p className="mt-2 text-xs leading-relaxed text-white/95">
//           Based on your spiritual profile, we recommend these Rudraksha beads.
//         </p>
//       </div>

//       <div className="space-y-3 max-h-[300px] overflow-y-auto">
//         {recommendations && recommendations.length > 0 ? (
//           recommendations.map((product, index) => (
//             <div
//               key={product._id || index}
//               onClick={() => handleProductClick(product)}
//               className="cursor-pointer rounded-lg border border-outline-variant/70 bg-surface-container-low p-3 transition-colors hover:bg-surface-container-low/80"
//             >
//               <div className="flex items-start gap-3">
//                 {product.image && (
//                   <img
//                     src={product.image}
//                     alt={product.name}
//                     className="h-12 w-12 rounded-lg object-cover"
//                   />
//                 )}
//                 <div className="flex-1">
//                   <h4 className="text-xs font-semibold text-on-surface">
//                     {product.name}
//                   </h4>
//                   <p className="mt-1 text-xs text-on-surface/70">
//                     {product.description || "Sacred Rudraksha bead"}
//                   </p>
//                   {product.price && (
//                     <p className="mt-1 text-xs font-medium text-primary">
//                       ₹{product.price}
//                     </p>
//                   )}
//                 </div>
//                 <ArrowRight className="h-4 w-4 text-on-surface/50" />
//               </div>
//             </div>
//           ))
//         ) : (
//           <p className="text-center text-xs text-on-surface/70">
//             No recommendations found. Try adjusting your preferences.
//           </p>
//         )}
//       </div>

//       <button
//         onClick={onReset}
//         className="flex w-full items-center justify-center gap-2 rounded-lg border border-outline-variant/70 bg-surface-container-low px-3 py-2 text-xs font-medium text-on-surface transition-colors hover:bg-surface-container-low/80"
//       >
//         <RotateCcw className="h-3 w-3" />
//         Start Over
//       </button>
//     </div>
//   );
// }

function RecommendationView({
  recommendations,
  onViewProduct,
  onReset,
  onNavigateToProduct,
}) {
  if (!recommendations) return null;

  const astrology = recommendations?.astrology || {};
  const quiz = recommendations?.quiz || {};
  const data = recommendations?.recommendations || {};

  const template = recommendations?.template;

  const rudraksha = data?.rudraksha || [];
  const gemstones = data?.gemstones || [];
  const products = data?.products || [];

  const hasAstrology =
    astrology?.rashi || astrology?.nakshatra || astrology?.rulingPlanet;

  const hasGoals =
    quiz?.primaryPurpose || (astrology?.spiritualTags?.length ?? 0) > 0;

  const hasRudraksha = rudraksha.length > 0;
  const hasGemstones = gemstones.length > 0;
  const hasProducts = products.length > 0;
  const hasReasoning = (astrology?.reasoning?.length ?? 0) > 0;
  const hasTemplate = !!template;

  const handleProductClick = (product) => {
    onNavigateToProduct(product);
    onViewProduct(product);
  };

  const whatsappUrl = generateShaktiWhatsappUrl(recommendations);
  return (
    <div className=" flex h-full max-h-[70vh] flex-col">
      <div className="flex-1 space-y-5 overflow-y-auto overflow-x-hidden pr-1">
        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-primary via-primary to-secondary p-5 text-white shadow-xl">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <h2 className="text-base font-bold">
              Your Personalized Spiritual Consultation
            </h2>
          </div>

          <p className="mt-3 text-xs leading-6 text-white/90">
            Based on your birth details and spiritual goals, Shakti AI has
            generated personalized recommendations for you.
          </p>
        </div>
        {/* Astrology */}
        {hasAstrology && (
          <div className="rounded-2xl border bg-surface-container-low p-4">
            <div className="mb-4 flex items-center gap-2">
              <Moon className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">Astrology Profile</h3>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {astrology?.rashi && (
                <div className="rounded-xl bg-surface p-3 text-center">
                  <p className="text-xs text-on-surface/60">Rashi</p>
                  <p className="font-semibold">{astrology.rashi}</p>
                </div>
              )}

              {astrology?.nakshatra && (
                <div className="rounded-xl bg-surface p-3 text-center">
                  <p className="text-xs text-on-surface/60">Nakshatra</p>
                  <p className="font-semibold">{astrology.nakshatra}</p>
                </div>
              )}

              {astrology?.rulingPlanet && (
                <div className="rounded-xl bg-surface p-3 text-center">
                  <p className="text-xs text-on-surface/60">Planet</p>
                  <p className="font-semibold">{astrology.rulingPlanet}</p>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Goals */}
        {hasGoals && (
          <div className="rounded-2xl border bg-surface-container-low p-4">
            <div className="mb-3 flex items-center gap-2">
              <Star className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">Your Spiritual Goal</h3>
            </div>

            {quiz?.primaryPurpose && (
              <p className="text-sm font-semibold capitalize">
                {quiz.primaryPurpose}
              </p>
            )}

            {astrology?.spiritualTags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {astrology.spiritualTags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-primary/10 px-3 py-1 text-xs text-primary"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Rudraksha */}
        {hasRudraksha && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">Recommended Rudraksha</h3>
            </div>

            {data.rudraksha.map((item) => (
              <div
                key={item.slug}
                className="rounded-2xl border bg-surface-container-low p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{item.name}</h4>

                    {item.deity && (
                      <p className="text-xs text-on-surface/60">{item.deity}</p>
                    )}
                  </div>

                  {item.score && (
                    <span className="rounded-full bg-primary px-3 py-1 text-xs text-white">
                      {item.score}%
                    </span>
                  )}
                </div>

                {item?.benefits?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium mb-2">Benefits</p>

                    <div className="flex flex-wrap gap-2">
                      {item.benefits.map((benefit) => (
                        <span
                          key={benefit}
                          className="rounded-full bg-green-100 px-2 py-1 text-[11px]"
                        >
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {item?.problems?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium mb-2">Helps With</p>

                    <div className="flex flex-wrap gap-2">
                      {item.problems.map((problem) => (
                        <span
                          key={problem}
                          className="rounded-full bg-red-100 px-2 py-1 text-[11px]"
                        >
                          {problem}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Gemstones */}
        {hasGemstones && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Gem className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">Recommended Gemstones</h3>
            </div>

            {data.gemstones.map((gem) => (
              <div
                key={gem.slug}
                className="rounded-2xl border bg-surface-container-low p-4 flex items-center justify-between"
              >
                <div>
                  <h4 className="font-semibold">{gem.name}</h4>

                  {gem.gods.length > 0 && (
                    <p className="text-xs text-on-surface/60">
                      {gem.gods.join(", ")}
                    </p>
                  )}
                </div>

                {gem.score > 0 && (
                  <span className="rounded-full bg-primary px-3 py-1 text-xs text-white">
                    {gem.score}%
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Products */}
        {hasProducts && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">Recommended Products</h3>
            </div>

            {data.products.length ? (
              data.products.map((product) => (
                <div
                  key={product._id}
                  onClick={() => handleProductClick(product)}
                  className="cursor-pointer rounded-2xl border p-3 hover:border-primary transition"
                >
                  <div className="flex justify-between">
                    <div>
                      <h4 className="font-semibold">{product.name}</h4>

                      <p className="text-xs text-on-surface/60">
                        {product.description}
                      </p>
                    </div>

                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed p-5 text-center">
                <Package className="mx-auto mb-3 h-8 w-8 text-primary" />

                <p className="font-medium">Matching products are coming soon</p>

                <p className="mt-2 text-xs text-on-surface/60">
                  Your spiritual recommendations have been generated
                  successfully. Product recommendations will appear here once
                  available.
                </p>
              </div>
            )}
          </div>
        )}
        {/* AI Explanation */}
        {hasTemplate && (
          <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <ScrollText className="h-4 w-4 text-primary" />

              <h3 className="font-semibold">Shakti AI Insight</h3>
            </div>

            <p className="text-sm leading-7">{template}</p>
          </div>
        )}
        {/* Reasoning */}
        {hasReasoning && (
          <div className="rounded-2xl border bg-surface-container-low p-4">
            <h3 className="font-semibold mb-3">Analysis Summary</h3>

            <div className="space-y-2">
              {astrology.reasoning.map((reason) => (
                <div key={reason} className="flex gap-2">
                  <div className="mt-1 h-2 w-2 rounded-full bg-primary" />

                  <p className="text-xs">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="shrink-0 border-t border-outline-variant/50 bg-surface-container-lowest pt-4">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onReset}
            className="rounded-xl border border-outline-variant bg-surface-container-low py-3 text-sm font-medium transition hover:bg-surface-container"
          >
            <RotateCcw className="mr-2 inline h-4 w-4" />
            New Consultation
          </button>

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 text-sm font-semibold text-white transition hover:bg-[#20BA5A]"
          >
            <MessageCircleMore className="h-4 w-4" />
            WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

function KnowledgeView({ tip }) {
  return (
    <div className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-4 text-white">
      <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em]">
        <BookOpen className="h-3.5 w-3.5" />
        Knowledge Base
      </div>
      <h4 className="mt-3 text-base font-bold">{tip.title}</h4>
      <p className="mt-2 text-xs leading-relaxed text-white/95">{tip.textEn}</p>
      <p className="mt-2 text-xs italic leading-relaxed text-white/90">
        {tip.textHi}
      </p>
    </div>
  );
}

export default function ShaktiBotFullPanel({ activeTab, onSetTab, tip }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(QUIZ_STEPS.WELCOME);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load saved state on mount
  useEffect(() => {
    const savedState = localStorage.getItem(QUIZ_STATE_KEY);
    const savedProfile = localStorage.getItem(USER_PROFILE_KEY);
    const savedRecommendations = localStorage.getItem(RECOMMENDATION_KEY);

    if (savedState) {
      const parsedState = JSON.parse(savedState);
      setQuizAnswers(parsedState.answers || {});
      setUserProfile(parsedState.userProfile || null);
      if (parsedState.step) {
        setCurrentStep(parsedState.step);
      }
    }

    if (savedProfile) {
      setUserProfile(JSON.parse(savedProfile));
    }

    if (savedRecommendations) {
      setRecommendations(JSON.parse(savedRecommendations));
    }
  }, []);

  // Save state to localStorage
  const saveState = (step, answers, profile) => {
    const stateToSave = {
      step,
      answers: answers || quizAnswers,
      userProfile: profile || userProfile,
    };
    localStorage.setItem(QUIZ_STATE_KEY, JSON.stringify(stateToSave));
  };

  const handleStartNew = () => {
    // Clear previous state
    localStorage.removeItem(QUIZ_STATE_KEY);
    localStorage.removeItem(RECOMMENDATION_KEY);
    localStorage.removeItem(USER_PROFILE_KEY);

    setQuizAnswers({});
    setUserProfile(null);
    setRecommendations([]);
    setCurrentStep(QUIZ_STEPS.USER_INFO);
  };

  const handleContinue = () => {
    const savedState = JSON.parse(localStorage.getItem(QUIZ_STATE_KEY));
    if (savedState.recommendations && savedState.recommendations.length > 0) {
      setRecommendations(savedState.recommendations);
      setCurrentStep(QUIZ_STEPS.RECOMMENDATIONS);
    } else if (savedState.userProfile) {
      setUserProfile(savedState.userProfile);
      setCurrentStep(QUIZ_STEPS.QUIZ);
    } else {
      setCurrentStep(QUIZ_STEPS.USER_INFO);
    }
  };

  const handleUserInfoSubmit = async (userData) => {
    setUserProfile(userData);
    localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(userData));

    setLoading(true);
    try {
      const questionsResponse = await recommendationService.getQuizQuestions();
      setQuizQuestions(questionsResponse.data || []);
      setCurrentStep(QUIZ_STEPS.QUIZ);
      saveState(QUIZ_STEPS.QUIZ, quizAnswers, userData);
    } catch (err) {
      setError("Failed to load quiz questions. Please try again.");
      console.error("Error loading quiz questions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuizAnswer = (questionKey, answer) => {
    const newAnswers = { ...quizAnswers, [questionKey]: answer };
    setQuizAnswers(newAnswers);
    saveState(currentStep, newAnswers, userProfile);
  };

  const handleQuizSubmit = async () => {
    setLoading(true);
    try {
      // Evaluate quiz answers
      const evaluatedAnswers = Object.values(quizAnswers);
      const evaluationResponse =
        await recommendationService.evaluateQuizAnswers(evaluatedAnswers);

      // Generate recommendations
      const recommendationData = {
        name: userProfile.name,
        dob: userProfile.dob,
        quizAnswers: evaluationResponse.data?.tags || evaluatedAnswers,
        limit: 5,
      };

      const recommendationsResponse =
        await recommendationService.generateRecommendations(recommendationData);

      const aiResponse = recommendationsResponse.data;

      setRecommendations(aiResponse);

      localStorage.setItem(RECOMMENDATION_KEY, JSON.stringify(aiResponse));

      setCurrentStep(QUIZ_STEPS.RECOMMENDATIONS);
      saveState(QUIZ_STEPS.RECOMMENDATIONS, quizAnswers, userProfile);
    } catch (err) {
      setError("Failed to generate recommendations. Please try again.");
      console.error("Error generating recommendations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    handleStartNew();
  };

  const handleViewProduct = (product) => {
    // Product view is handled by the navigation in RecommendationView
    console.log("Viewing product:", product);
  };

  const handleNavigateToProduct = (product) => {
    if (product._id) {
      navigate(
        `/product/${product.categoryId || "rudraksha"}/${product.subCategoryId || "all"}/${product._id}`,
      );
    }
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="space-y-4">
          <div className="rounded-lg bg-error-container p-3 text-error text-xs">
            {error}
          </div>
          <button
            onClick={handleReset}
            className="w-full rounded-lg bg-primary px-3 py-2 text-xs font-medium text-white"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (loading) {
      return <LoadingView />;
    }

    switch (currentStep) {
      case QUIZ_STEPS.WELCOME:
        return (
          <WelcomeScreen
            onStartNew={handleStartNew}
            onContinue={handleContinue}
          />
        );

      case QUIZ_STEPS.USER_INFO:
        return (
          <UserInfoForm
            onSubmit={handleUserInfoSubmit}
            onCancel={() => setCurrentStep(QUIZ_STEPS.WELCOME)}
            initialData={userProfile}
          />
        );

      case QUIZ_STEPS.QUIZ:
        return (
          <QuizView
            questions={quizQuestions}
            answers={quizAnswers}
            onAnswer={handleQuizAnswer}
            onBack={() => setCurrentStep(QUIZ_STEPS.USER_INFO)}
            onSubmit={handleQuizSubmit}
          />
        );

      case QUIZ_STEPS.RECOMMENDATIONS:
        return (
          <RecommendationView
            recommendations={recommendations}
            onViewProduct={handleViewProduct}
            onReset={handleReset}
            onNavigateToProduct={handleNavigateToProduct}
          />
        );

      default:
        return (
          <WelcomeScreen
            onStartNew={handleStartNew}
            onContinue={handleContinue}
          />
        );
    }
  };

  return (
    <div className="mb-4 w-[360px] max-w-[calc(100vw-2rem)] rounded-3xl border border-outline-variant/70 bg-surface-container-lowest p-3 shadow-[0_24px_60px_rgba(27,28,23,0.14)]">
      <div className="mb-3 inline-flex w-full rounded-full bg-surface-container-low p-1">
        <button
          type="button"
          onClick={() => onSetTab("knowledge")}
          className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
            activeTab === "knowledge"
              ? "bg-primary text-white"
              : "text-on-surface/80"
          }`}
        >
          <BookOpen className="h-3.5 w-3.5" />
          Knowledge Base
        </button>
        <button
          type="button"
          onClick={() => {
            setCurrentStep(QUIZ_STEPS.WELCOME);
            onSetTab("chat");
          }}
          className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
            activeTab === "chat"
              ? "bg-primary text-white"
              : "text-on-surface/80"
          }`}
        >
          <Sparkles className="h-3.5 w-3.5" />
          Shakti AI
        </button>
      </div>

      {activeTab === "chat" ? renderContent() : <KnowledgeView tip={tip} />}
    </div>
  );
}
